import { formatNumber } from '@/utils/utils'
import * as echarts from 'echarts'
import { EChartsOption } from 'echarts'
import { chartColors } from './COLOR_PALETTE'

export const SALES_METRIC_FILTERS: { value: string; label: string }[] = [
  {
    value: 'adns',
    label: 'ADNS',
  },
  {
    value: 'traffic',
    label: 'Traffic',
  },
  {
    value: 'aov',
    label: 'AoV',
  },
  {
    value: 'discounts',
    label: 'Discounts',
  },
]

export const LABOR_METRIC_FILTERS: { value: string; label: string }[] = [
  {
    value: 'laborRatio',
    label: 'Labor Ratio',
  },
  {
    value: 'laborHours',
    label: 'Labor Hours',
  },
  {
    value: 'unitLaborCost',
    label: 'Unit Labor Cost',
  },
]

export const SPEED_OF_SERVICE_METRIC_FILTERS: {
  value: string
  label: string
}[] = [
  {
    value: 'speedOfService',
    label: 'Speed of Service',
  },
  {
    value: 'utilization',
    label: 'Utilization',
  },
]

export const SPEND_METRIC_FILTERS: { value: string; label: string }[] = [
  {
    value: 'spend',
    label: 'Spend',
  },
  {
    value: 'ptiImpact',
    label: 'PTI Impact',
  },
  {
    value: 'ptiPercentage',
    label: 'PTI %',
  },
]

export const PNL_METRIC_FILTERS: { value: string; label: string }[] = [
  {
    value: 'salesAmount',
    label: 'Sales',
  },
  {
    value: 'cogsAmount',
    label: 'COGS',
  },
  {
    value: 'storeLaborAmount',
    label: 'Store Labor',
  },
  {
    value: 'opExAmount',
    label: 'OpEx',
  },
  {
    value: 'rentAndOccupancyAmount',
    label: 'Rent and Occupancy',
  },
  {
    value: 'overheadAmount',
    label: 'Overhead',
  },
  {
    value: 'ebitdaAmount',
    label: 'EBITDA',
  },
]

export const DEFAULT_CHART_OPTIONS: EChartsOption = {
  dataZoom: [
    {
      type: 'slider',
      start: 0,
      end: 100,
    },
  ],
  tooltip: {
    trigger: 'axis',
    valueFormatter: (value: any) => {
      if (typeof value === 'number') {
        return value.toFixed(0)
      }
      return value
    },
  },
  legend: {
    data: [],
  },
  grid: {
    containLabel: true,
    top: 30,
    left: 10,
    right: 10,
  },
  toolbox: {
    feature: {
      saveAsImage: {},
      dataZoom: {},
    },
  },
  xAxis: {
    type: 'category',
    boundaryGap: false,
    data: [],
  },
  yAxis: {
    type: 'value',
  },
  animationDuration: 700,
  animationEasing: 'cubicOut',
}

// TODO: make it useful for all bar charts
export const barChartOptions = (
  rawData: number[][],
  seriesNames: string[],
  xAxisNames: string[],
  typeFilter?: 'money' | 'number' | 'percentage',
  crossLabels?: boolean
): EChartsOption => {
  const series: echarts.BarSeriesOption[] = seriesNames.map((name, sid) => {
    return {
      name,
      type: 'bar',
      barWidth: '25%',
      data: rawData[sid],
      itemStyle: {
        borderRadius: 8,
        borderWidth: 1,
        borderColor: '#fff',
        color: chartColors[sid % chartColors.length],
      },
    }
  })

  // Only show legend if there are multiple series
  const showLegend = seriesNames.length > 1

  return {
    ...(showLegend && {
      legend: {
        selectedMode: false,
        orient: 'horizontal',
        top: '0%',
        left: 'center',
        data: seriesNames,
        icon: 'circle',
        itemGap: 20,
      },
    }),
    tooltip: {
      trigger: 'item',
      formatter: (params: any) => {
        const formattedValue =
          typeFilter === 'money'
            ? formatNumber(params.value, 'money')
            : typeFilter === 'percentage'
              ? formatNumber(params.value, 'percentage')
              : params.value.toLocaleString()
        return `${params.name}<br/>${seriesNames.length > 1 ? params.seriesName + ': ' : ''}${formattedValue}`
      },
    },
    grid: {
      top: showLegend ? '15%' : '10%',
      bottom: '20%',
      left: '12%',
      right: '4%',
    },
    yAxis: {
      type: 'value',
      axisLabel: {
        formatter: typeFilter === 'money' ? '${value}' : '{value}',
      },
    },
    xAxis: {
      type: 'category',
      data: xAxisNames,
      axisLine: {
        show: false,
      },
      axisTick: {
        show: false,
      },
      axisLabel: {
        rotate: crossLabels ? 45 : xAxisNames.length > 5 ? 45 : 0,
        interval: 0,
        formatter: (value: string) => {
          return value.length > 10 ? value.substring(0, 10) + '...' : value
        },
      },
    },
    series,
  }
}

export const stackedBarChartOptions = (
  rawData: number[][],
  seriesNames: string[],
  xAxisNames: string[],
  barWidth: string = '60%',
  typeFilter?: 'money' | 'number' | 'hour' | 'percentage'
): EChartsOption => {
  const totalData: number[] = []
  for (let i = 0; i < rawData[0].length; ++i) {
    let sum = 0
    for (let j = 0; j < rawData.length; ++j) {
      sum += rawData[j][i]
    }
    totalData.push(sum)
  }

  const hasLegendOverflow = seriesNames.length > 3

  const series: echarts.BarSeriesOption[] = seriesNames.map((name, sid) => {
    return {
      name,
      type: 'bar',
      stack: 'total',
      barWidth: barWidth,
      data: rawData[sid],
      itemStyle: {
        borderRadius: 8,
        borderWidth: 1,
        borderColor: '#fff',
      },
    }
  })

  return {
    legend: {
      selectedMode: false,
      orient: 'horizontal',
      top: '0%',
      left: 'center',
      data: seriesNames,
      icon: 'circle',
      itemGap: hasLegendOverflow ? 15 : 20,
      type: hasLegendOverflow ? 'scroll' : 'plain',
      pageButtonItemGap: 5,
      pageButtonGap: 5,
      pageButtonPosition: 'end',
      pageIconColor: '#666',
      pageIconInactiveColor: '#ccc',
      pageIconSize: 12,
      pageTextStyle: {
        color: '#666',
      },
    },
    tooltip: {
      trigger: 'axis',
      formatter: stackValueFormatter(typeFilter ?? 'number'),
    },
    grid: {
      bottom: '20%',
      left: '12%',
      top: hasLegendOverflow ? '15%' : '10%',
    },
    yAxis: {
      type: 'value',
    },
    center: ['50%', '60%'],
    xAxis: {
      type: 'category',
      data: xAxisNames,
      axisLine: {
        show: false,
      },
      axisTick: {
        show: false,
      },
    },
    series: series.map((s, index) => ({
      ...s,
      itemStyle: {
        ...s.itemStyle,
        color: chartColors[index % chartColors.length],
      },
    })),
  }
}

export const lineChartOptions = (
  rawData: number[][],
  seriesNames: string[],
  xAxisNames: string[],
  typeFilter?: 'money' | 'number' | 'percentage'
): EChartsOption => {
  // Safety check for empty data
  if (!seriesNames.length || !rawData.length || !xAxisNames.length) {
    return {
      grid: { top: '10%', bottom: '10%', left: '10%', right: '10%' },
      xAxis: { type: 'category', data: [] },
      yAxis: { type: 'value' },
      series: [],
    }
  }

  const series: echarts.LineSeriesOption[] = seriesNames.map((name, sid) => {
    return {
      name,
      type: 'line',
      data: rawData[sid] || [],
      itemStyle: {
        color: chartColors[sid % chartColors.length],
      },
      lineStyle: {
        color: chartColors[sid % chartColors.length],
        width: 2,
      },
      symbol: 'circle',
      symbolSize: 6,
    }
  })

  // Only show legend if there are multiple series
  const showLegend = seriesNames.length > 1

  return {
    ...(showLegend && {
      legend: {
        selectedMode: false,
        orient: 'horizontal',
        top: '0%',
        left: 'center',
        data: seriesNames,
        icon: 'circle',
        itemGap: 20,
      },
    }),
    tooltip: {
      trigger: 'axis',
      formatter: lineValueFormatter(typeFilter ?? 'number'),
    },
    grid: {
      bottom: '20%',
      left: '12%',
      right: '4%',
      top: showLegend ? '15%' : '10%',
    },
    yAxis: {
      type: 'value',
      axisLabel: {
        formatter: typeFilter === 'money' ? '${value}' : '{value}',
      },
    },
    xAxis: {
      type: 'category',
      data: xAxisNames,
      axisLine: {
        show: false,
      },
      axisTick: {
        show: false,
      },
    },
    series,
  }
}

export const pieChartOptions = (
  rawData: number[],
  seriesNames: string[],
  typeFilter?: 'money' | 'percentage'
): EChartsOption => {
  const hasLegendOverflow = seriesNames.length > 3

  const series: echarts.PieSeriesOption[] = [
    {
      type: 'pie',
      radius: ['30%', '60%'],
      center: ['50%', '35%'],
      data: seriesNames.map((category, index) => ({
        name: category,
        value: rawData[index] || 0,
        itemStyle: {
          color: chartColors[index % chartColors.length],
          borderRadius: 8,
          borderWidth: 2,
          borderColor: '#fff',
        },
      })),
      emphasis: {
        itemStyle: {
          shadowBlur: 1,
          shadowOffsetX: 0,
          shadowColor: 'rgba(0, 0, 0, 0.5)',
          borderRadius: 8,
          borderWidth: 1,
        },
      },
    },
  ]

  return {
    tooltip: {
      trigger: 'item',
      formatter: valueFormatter(typeFilter ?? 'number'),
    },
    legend: {
      orient: hasLegendOverflow ? 'horizontal' : 'horizontal',
      bottom: hasLegendOverflow ? '2%' : '5%',
      left: 'center',
      data: seriesNames,
      icon: 'circle',
      itemGap: hasLegendOverflow ? 15 : 20,
      type: hasLegendOverflow ? 'scroll' : 'plain',
      pageButtonItemGap: 5,
      pageButtonGap: 5,
      pageButtonPosition: 'end',
      pageIconColor: '#666',
      pageIconInactiveColor: '#ccc',
      pageIconSize: 12,
      pageTextStyle: {
        color: '#666',
      },
    },
    grid: {
      bottom: hasLegendOverflow ? '15%' : '0%',
      top: '40%',
    },
    series,
  }
}

export const scatterChartOptions = (
  data: {
    name: string
    x: number
    y: number
    info: string
  }[],
  xAxis: { name: string; type?: 'money' | 'number' | 'percentage' },
  yAxis: { name: string; type?: 'money' | 'number' | 'percentage' }
): EChartsOption => {
  return {
    grid: {
      top: '10%',
      bottom: '25%',
      left: '10%',
      right: '10%',
    },
    dataZoom: [
      {
        type: 'slider',
        xAxisIndex: 0,
        start: 0,
        end: 100,
        bottom: 15,
        height: 15,
      },
      {
        type: 'slider',
        yAxisIndex: 0,
        start: 0,
        end: 100,
        right: 15,
        width: 20,
      },
    ],
    toolbox: {
      feature: {
        saveAsImage: {},
        dataZoom: {},
      },
    },
    tooltip: {
      trigger: 'item',
      confine: true,
      formatter: (params: any) => {
        const item = params.data
        const xType = xAxis.type ?? 'number'
        const yType = yAxis.type ?? 'number'

        const formattedX =
          xType === 'money'
            ? formatNumber(item.value[0], 'money')
            : xType === 'percentage'
              ? formatNumber(item.value[0], 'percentage')
              : item.value[0].toLocaleString()

        const formattedY =
          yType === 'money'
            ? formatNumber(item.value[1], 'money')
            : yType === 'percentage'
              ? formatNumber(item.value[1], 'percentage')
              : item.value[1].toLocaleString()

        const name = item.name
        const maxLineLength = 30
        let wrappedName = ''
        if (name.length > maxLineLength) {
          const words = name.split(' ')
          let currentLine = ''
          words.forEach((word: string) => {
            if ((currentLine + word).length < maxLineLength) {
              currentLine += (currentLine ? ' ' : '') + word
            } else {
              wrappedName += (wrappedName ? '<br/>' : '') + currentLine
              currentLine = word
            }
          })
          wrappedName += (wrappedName ? '<br/>' : '') + currentLine
        } else {
          wrappedName = name
        }

        return `${wrappedName}<br/>${xAxis.name}: ${formattedX}<br/>${yAxis.name}: ${formattedY}`
      },
    },
    xAxis: {
      name: xAxis.name,
      nameLocation: 'middle',
      nameGap: 30,
      type: 'value',
      // scale: true,
      splitLine: {
        show: true,
      },
      axisLabel: {
        formatter: (value: number) => {
          if (xAxis.type === 'money') {
            return `$${value >= 1000 ? (value / 1000).toFixed(0) + 'k' : value}`
          }
          return value >= 1000 ? (value / 1000).toFixed(0) + 'k' : `${value}`
        },
      },
    },
    yAxis: {
      name: yAxis.name,
      type: 'value',
      // scale: true,
      splitLine: {
        show: true,
      },
      axisLabel: {
        formatter: (value: number) => {
          if (yAxis.type === 'money') {
            return `$${value >= 1000 ? (value / 1000).toFixed(0) + 'k' : value}`
          }
          return value >= 1000 ? (value / 1000).toFixed(0) + 'k' : `${value}`
        },
      },
    },
    series: [
      {
        type: 'scatter',
        symbolSize: 12,
        itemStyle: {
          color: (params: any) => {
            return chartColors[params.dataIndex % chartColors.length]
          },
        },
        data: data.map((item) => ({
          name: item.name,
          value: [item.x, item.y],
          info: item.info,
        })),
      },
    ],
  }
}

const valueFormatter = (typeFilter: 'money' | 'percentage' | 'number') => {
  if (typeFilter === 'money') {
    return (params: any) => {
      const formattedValue = formatNumber(params.value, 'money')
      return `${params.name}: ${formattedValue} (${params.percent}%)`
    }
  } else if (typeFilter === 'percentage') {
    return (params: any) => {
      const formattedValue = formatNumber(params.value, 'percentage')
      return `${params.name}: ${formattedValue} (${params.percent}%)`
    }
  } else {
    return (params: any) => {
      return `${params.name}: ${params.value} (${params.percent}%)`
    }
  }
}

const stackValueFormatter = (
  typeFilter: 'money' | 'number' | 'hour' | 'percentage'
) => {
  return (params: any) => {
    let result = ''
    params.forEach((param: any) => {
      const formattedValue = formatNumber(param.value, typeFilter)
      result += `${param.seriesName}: ${formattedValue}<br/>`
    })
    return result
  }
}

export const waterfallChartOptions = (
  rawData: number[],
  xAxisNames: string[],
  typeFilter?: 'money' | 'number'
): EChartsOption => {
  // Safety check for empty data
  if (!rawData.length || !xAxisNames.length) {
    return {
      grid: { top: '10%', bottom: '10%', left: '10%', right: '10%' },
      xAxis: { type: 'category', data: [] },
      yAxis: { type: 'value' },
      series: [],
    }
  }

  // Calculate cumulative values for waterfall effect
  const cumulativeData: number[] = []
  const placeholderData: number[] = []
  let cumulative = 0

  rawData.forEach((value, index) => {
    if (index === 0) {
      // First bar starts from zero
      placeholderData.push(0)
      cumulativeData.push(value)
      cumulative = value
    } else {
      // Subsequent bars stack on previous cumulative value
      const previousCumulative = cumulative
      cumulative += value

      if (value >= 0) {
        // Positive values: placeholder starts from previous cumulative
        placeholderData.push(previousCumulative)
        cumulativeData.push(value)
      } else {
        // Negative values: placeholder starts from new cumulative, bar goes up
        placeholderData.push(cumulative)
        cumulativeData.push(Math.abs(value))
      }
    }
  })

  return {
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'shadow',
      },
      formatter: (params: any) => {
        const dataIndex = params[0].dataIndex
        const originalValue = rawData[dataIndex]
        const formattedValue =
          typeFilter === 'money'
            ? formatNumber(Math.abs(originalValue), 'money')
            : Math.abs(originalValue).toLocaleString()

        return `${xAxisNames[dataIndex]}<br/>Value: ${formattedValue}`
      },
    },
    grid: {
      bottom: '5%',
      left: '10%',
      right: '4%',
      top: '10%',
      containLabel: true,
    },
    xAxis: {
      type: 'category',
      data: xAxisNames,
      axisLine: {
        show: false,
      },
      axisTick: {
        show: false,
      },
      axisLabel: {
        rotate: 45,
        interval: 0,
      },
    },
    yAxis: {
      type: 'value',
      axisLabel: {
        formatter: typeFilter === 'money' ? '${value}' : '{value}',
      },
    },
    series: [
      {
        name: 'Placeholder',
        type: 'bar',
        stack: 'waterfall',
        itemStyle: {
          borderColor: 'transparent',
          color: 'transparent',
        },
        emphasis: {
          itemStyle: {
            borderColor: 'transparent',
            color: 'transparent',
          },
        },
        data: placeholderData,
      },
      {
        name: 'Value',
        type: 'bar',
        stack: 'waterfall',
        itemStyle: {
          borderRadius: 8,
          borderWidth: 1,
          borderColor: '#fff',
          color: (params: any) => {
            const originalValue = rawData[params.dataIndex]
            // Negative values are always red
            if (originalValue < 0) {
              return chartColors[3] // Red
            }
            // First bar (starting value) uses primary color
            if (params.dataIndex === 0) {
              return chartColors[0] // Blue
            }
            // Positive values use green
            return chartColors[1] // Green
          },
        },
        label: {
          show: true,
          position: 'top',
          formatter: (params: any) => {
            const originalValue = rawData[params.dataIndex]
            return typeFilter === 'money'
              ? formatNumber(Math.abs(originalValue), 'money')
              : Math.abs(originalValue).toLocaleString()
          },
        },
        data: cumulativeData,
      },
    ],
  }
}

const lineValueFormatter = (typeFilter: 'money' | 'number' | 'percentage') => {
  if (typeFilter === 'money') {
    return (params: any) => {
      let result = `${params[0].axisValue}<br/>`
      params.forEach((param: any) => {
        const formattedValue = formatNumber(param.value, 'money')
        result += `${param.marker} ${param.seriesName}: ${formattedValue}<br/>`
      })
      return result
    }
  } else if (typeFilter === 'percentage') {
    return (params: any) => {
      let result = `${params[0].axisValue}<br/>`
      params.forEach((param: any) => {
        const formattedValue = formatNumber(param.value, 'percentage')
        result += `${param.marker} ${param.seriesName}: ${formattedValue}<br/>`
      })
      return result
    }
  } else {
    return (params: any) => {
      let result = `${params[0].axisValue}<br/>`
      params.forEach((param: any) => {
        result += `${param.marker} ${param.seriesName}: ${param.value}<br/>`
      })
      return result
    }
  }
}
